import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Sun, Moon, Monitor, Globe, Bell, BellOff, 
  Mail, MessageSquare, User, Palette, Languages, Shield,
  Check, ChevronRight, Smartphone, Camera, Loader2, RefreshCw, X, Edit2, BellRing
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage, SUPPORTED_LANGUAGES } from '../contexts/LanguageContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import AvatarUploader from '../components/common/AvatarUploader';
import { VerificationCodeInput } from '../components/common/VerificationCodeInput';
import { PushNotificationToggle } from '../components/common/PushNotificationToggle';

// Translations
const translations = {
  pageTitle: { 'pt-BR': 'Configurações', 'en-US': 'Settings' },
  pageDescription: { 'pt-BR': 'Gerencie suas preferências pessoais', 'en-US': 'Manage your personal preferences' },
  back: { 'pt-BR': 'Voltar', 'en-US': 'Back' },
  
  // Profile section
  profileTitle: { 'pt-BR': 'Perfil', 'en-US': 'Profile' },
  profileDescription: { 'pt-BR': 'Suas informações pessoais', 'en-US': 'Your personal information' },
  name: { 'pt-BR': 'Nome', 'en-US': 'Name' },
  email: { 'pt-BR': 'Email', 'en-US': 'Email' },
  memberSince: { 'pt-BR': 'Membro desde', 'en-US': 'Member since' },
  changeEmail: { 'pt-BR': 'Alterar email', 'en-US': 'Change email' },
  emailVerified: { 'pt-BR': 'Email verificado', 'en-US': 'Email verified' },
  emailNotVerified: { 'pt-BR': 'Email não verificado', 'en-US': 'Email not verified' },
  
  // Email change modal
  changeEmailTitle: { 'pt-BR': 'Alterar Email', 'en-US': 'Change Email' },
  changeEmailDescription: { 'pt-BR': 'Digite seu novo endereço de email', 'en-US': 'Enter your new email address' },
  newEmail: { 'pt-BR': 'Novo email', 'en-US': 'New email' },
  sendCode: { 'pt-BR': 'Enviar código', 'en-US': 'Send code' },
  verifyEmail: { 'pt-BR': 'Verificar Email', 'en-US': 'Verify Email' },
  verifyEmailDescription: { 'pt-BR': 'Digite o código de 6 dígitos enviado para', 'en-US': 'Enter the 6-digit code sent to' },
  resendCode: { 'pt-BR': 'Reenviar código', 'en-US': 'Resend code' },
  backToEmail: { 'pt-BR': 'Usar outro email', 'en-US': 'Use another email' },
  emailChangedSuccess: { 'pt-BR': 'Email alterado com sucesso!', 'en-US': 'Email changed successfully!' },
  
  // Appearance section
  appearanceTitle: { 'pt-BR': 'Aparência', 'en-US': 'Appearance' },
  appearanceDescription: { 'pt-BR': 'Personalize a aparência do aplicativo', 'en-US': 'Customize the app appearance' },
  theme: { 'pt-BR': 'Tema', 'en-US': 'Theme' },
  themeDescription: { 'pt-BR': 'Escolha entre claro, escuro ou automático', 'en-US': 'Choose between light, dark or automatic' },
  light: { 'pt-BR': 'Claro', 'en-US': 'Light' },
  dark: { 'pt-BR': 'Escuro', 'en-US': 'Dark' },
  system: { 'pt-BR': 'Sistema', 'en-US': 'System' },
  systemDescription: { 'pt-BR': 'Segue a preferência do dispositivo', 'en-US': 'Follows device preference' },
  
  // Language section
  languageTitle: { 'pt-BR': 'Idioma', 'en-US': 'Language' },
  languageDescription: { 'pt-BR': 'Escolha o idioma da interface', 'en-US': 'Choose the interface language' },
  
  // Notifications section
  notificationsTitle: { 'pt-BR': 'Notificações', 'en-US': 'Notifications' },
  notificationsDescription: { 'pt-BR': 'Configure como você recebe notificações', 'en-US': 'Configure how you receive notifications' },
  pushNotifications: { 'pt-BR': 'Notificações Push', 'en-US': 'Push Notifications' },
  pushDescription: { 'pt-BR': 'Receba alertas mesmo com o app fechado', 'en-US': 'Receive alerts even with the app closed' },
  emailNotifications: { 'pt-BR': 'Notificações por Email', 'en-US': 'Email Notifications' },
  emailDescription: { 'pt-BR': 'Receba resumos e alertas importantes por email', 'en-US': 'Receive summaries and important alerts by email' },
  tripReminders: { 'pt-BR': 'Lembretes de Viagem', 'en-US': 'Trip Reminders' },
  tripRemindersDescription: { 'pt-BR': 'Receba lembretes sobre pagamentos e atividades', 'en-US': 'Receive reminders about payments and activities' },
  
  // PWA section
  pwaTitle: { 'pt-BR': 'Aplicativo', 'en-US': 'App' },
  pwaDescription: { 'pt-BR': 'Instale o ViajeJunto como aplicativo', 'en-US': 'Install ViajeJunto as an app' },
  installApp: { 'pt-BR': 'Instalar Aplicativo', 'en-US': 'Install App' },
  appInstalled: { 'pt-BR': 'Aplicativo já instalado', 'en-US': 'App already installed' },
  
  // Toasts
  savedSuccess: { 'pt-BR': 'Preferências salvas!', 'en-US': 'Preferences saved!' },
  themeChanged: { 'pt-BR': 'Tema alterado para', 'en-US': 'Theme changed to' },
  languageChanged: { 'pt-BR': 'Idioma alterado para', 'en-US': 'Language changed to' },
};

const ThemeOption = ({ value, currentValue, onClick, icon: Icon, label, description, t }) => {
  const isSelected = value === currentValue;
  
  return (
    <button
      onClick={() => onClick(value)}
      className={`
        flex items-center gap-3 p-4 rounded-xl border-2 transition-all w-full text-left
        ${isSelected 
          ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20' 
          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
        }
      `}
      data-testid={`theme-option-${value}`}
    >
      <div className={`
        w-10 h-10 rounded-lg flex items-center justify-center
        ${isSelected 
          ? 'bg-orange-500 text-white' 
          : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
        }
      `}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <p className={`font-medium ${isSelected ? 'text-orange-600 dark:text-orange-400' : 'text-slate-900 dark:text-white'}`}>
          {label}
        </p>
        {description && (
          <p className="text-sm text-slate-500 dark:text-slate-400">{description}</p>
        )}
      </div>
      {isSelected && (
        <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center">
          <Check className="w-4 h-4 text-white" />
        </div>
      )}
    </button>
  );
};

const LanguageOption = ({ code, currentValue, onClick, t }) => {
  const isSelected = code === currentValue;
  const lang = code === 'system' ? null : SUPPORTED_LANGUAGES[code];
  
  return (
    <button
      onClick={() => onClick(code)}
      className={`
        flex items-center gap-3 p-4 rounded-xl border-2 transition-all w-full text-left
        ${isSelected 
          ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20' 
          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
        }
      `}
      data-testid={`language-option-${code}`}
    >
      <div className={`
        w-10 h-10 rounded-lg flex items-center justify-center text-xl
        ${isSelected 
          ? 'bg-orange-500' 
          : 'bg-slate-100 dark:bg-slate-800'
        }
      `}>
        {code === 'system' ? <Globe className="w-5 h-5 text-slate-600 dark:text-slate-400" /> : lang?.flag}
      </div>
      <div className="flex-1">
        <p className={`font-medium ${isSelected ? 'text-orange-600 dark:text-orange-400' : 'text-slate-900 dark:text-white'}`}>
          {code === 'system' ? t(translations.system) : lang?.name}
        </p>
        {code === 'system' && (
          <p className="text-sm text-slate-500 dark:text-slate-400">{t(translations.systemDescription)}</p>
        )}
      </div>
      {isSelected && (
        <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center">
          <Check className="w-4 h-4 text-white" />
        </div>
      )}
    </button>
  );
};

const NotificationToggle = ({ icon: Icon, label, description, checked, onChange, testId }) => {
  return (
    <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
          <Icon className="w-5 h-5 text-slate-600 dark:text-slate-400" />
        </div>
        <div>
          <p className="font-medium text-slate-900 dark:text-white">{label}</p>
          <p className="text-sm text-slate-500 dark:text-slate-400">{description}</p>
        </div>
      </div>
      <Switch 
        checked={checked} 
        onCheckedChange={onChange}
        data-testid={testId}
      />
    </div>
  );
};

export default function UserSettings() {
  const navigate = useNavigate();
  const { user, api, refreshUser } = useAuth();
  const { themePreference, setTheme } = useTheme();
  const { languagePreference, setLanguage, t } = useLanguage();
  
  // Email change modal state
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailModalStep, setEmailModalStep] = useState('form'); // 'form' or 'verification'
  const [newEmail, setNewEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationError, setVerificationError] = useState(null);
  
  // Notification preferences (stored in localStorage for now)
  const [notifications, setNotifications] = useState(() => {
    const saved = localStorage.getItem('viajejunto-notification-prefs');
    return saved ? JSON.parse(saved) : {
      push: true,
      email: true,
      tripReminders: true
    };
  });
  
  // PWA install state - check initial state synchronously
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(() => {
    // Check if app is already installed on initial render
    if (typeof window !== 'undefined') {
      return window.matchMedia('(display-mode: standalone)').matches;
    }
    return false;
  });
  
  useEffect(() => {
    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      window.deferredPrompt = e;
      setCanInstall(true);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);
  
  // Reset email modal when closed
  useEffect(() => {
    if (!emailModalOpen) {
      setEmailModalStep('form');
      setNewEmail('');
      setVerificationError(null);
    }
  }, [emailModalOpen]);
  
  // Email change handlers
  const handleInitEmailChange = async (e) => {
    e.preventDefault();
    setEmailLoading(true);
    
    try {
      const response = await api.post('/auth/email/change/init', { new_email: newEmail });
      setEmailModalStep('verification');
      toast.success('Código enviado para o novo email! 📧');
      
      // Show dev code in console
      if (response.data.dev_code) {
        console.log('DEV CODE:', response.data.dev_code);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao enviar código';
      toast.error(message);
    } finally {
      setEmailLoading(false);
    }
  };
  
  const handleVerifyEmailChange = async (code) => {
    setVerificationLoading(true);
    setVerificationError(null);
    
    try {
      await api.post('/auth/email/change/verify', { 
        new_email: newEmail, 
        code: code 
      });
      
      toast.success(t(translations.emailChangedSuccess));
      setEmailModalOpen(false);
      
      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Código inválido';
      setVerificationError(message);
    } finally {
      setVerificationLoading(false);
    }
  };
  
  const handleResendEmailCode = async () => {
    setEmailLoading(true);
    setVerificationError(null);
    
    try {
      const response = await api.post('/auth/email/change/resend', { new_email: newEmail });
      toast.success('Novo código enviado! 📧');
      
      if (response.data.dev_code) {
        console.log('DEV CODE:', response.data.dev_code);
      }
    } catch (error) {
      toast.error('Erro ao reenviar código');
    } finally {
      setEmailLoading(false);
    }
  };
  
  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    const themeNames = {
      light: t(translations.light),
      dark: t(translations.dark),
      system: t(translations.system)
    };
    toast.success(`${t(translations.themeChanged)} ${themeNames[newTheme]}`);
  };
  
  const handleLanguageChange = (newLang) => {
    setLanguage(newLang);
    const langNames = {
      system: t(translations.system),
      'pt-BR': 'Português',
      'en-US': 'English'
    };
    toast.success(`${t(translations.languageChanged)} ${langNames[newLang]}`);
  };
  
  const handleNotificationChange = (key, value) => {
    const newPrefs = { ...notifications, [key]: value };
    setNotifications(newPrefs);
    localStorage.setItem('viajejunto-notification-prefs', JSON.stringify(newPrefs));
    toast.success(t(translations.savedSuccess));
  };
  
  const handleInstallApp = async () => {
    if (window.deferredPrompt) {
      window.deferredPrompt.prompt();
      const { outcome } = await window.deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
        setCanInstall(false);
      }
      window.deferredPrompt = null;
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(languagePreference === 'en-US' ? 'en-US' : 'pt-BR', {
      year: 'numeric',
      month: 'long'
    });
  };

  return (
    <DashboardLayout title={t(translations.pageTitle)}>
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4"
        >
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              {t(translations.pageTitle)}
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              {t(translations.pageDescription)}
            </p>
          </div>
        </motion.div>

        {/* Profile Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-orange-500" />
                <CardTitle>{t(translations.profileTitle)}</CardTitle>
              </div>
              <CardDescription>{t(translations.profileDescription)}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <AvatarUploader 
                  currentAvatarUrl={user?.avatar_url}
                  currentEmoji={user?.avatar_emoji || '😊'}
                  onAvatarChange={async (url) => {
                    // Refresh user data to update avatar everywhere
                    if (refreshUser) {
                      await refreshUser();
                    }
                  }}
                />
                <div className="flex-1">
                  <p className="font-semibold text-lg text-slate-900 dark:text-white">{user?.name}</p>
                  <div className="flex items-center gap-2">
                    <p className="text-slate-500 dark:text-slate-400">{user?.email}</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                      onClick={() => setEmailModalOpen(true)}
                      data-testid="change-email-btn"
                    >
                      <Edit2 className="w-3 h-3 mr-1" />
                      {t(translations.changeEmail)}
                    </Button>
                  </div>
                  {user?.email_verified ? (
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      {t(translations.emailVerified)}
                    </p>
                  ) : (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      {t(translations.emailNotVerified)}
                    </p>
                  )}
                  <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                    {t(translations.memberSince)} {formatDate(user?.created_at)}
                  </p>
                  <p className="text-xs text-orange-500 mt-1">
                    <Camera className="w-3 h-3 inline mr-1" />
                    Passe o mouse sobre o avatar para trocar
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Appearance Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Palette className="w-5 h-5 text-orange-500" />
                <CardTitle>{t(translations.appearanceTitle)}</CardTitle>
              </div>
              <CardDescription>{t(translations.appearanceDescription)}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <ThemeOption
                value="light"
                currentValue={themePreference}
                onClick={handleThemeChange}
                icon={Sun}
                label={t(translations.light)}
                t={t}
              />
              <ThemeOption
                value="dark"
                currentValue={themePreference}
                onClick={handleThemeChange}
                icon={Moon}
                label={t(translations.dark)}
                t={t}
              />
              <ThemeOption
                value="system"
                currentValue={themePreference}
                onClick={handleThemeChange}
                icon={Monitor}
                label={t(translations.system)}
                description={t(translations.systemDescription)}
                t={t}
              />
            </CardContent>
          </Card>
        </motion.div>

        {/* Language Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Languages className="w-5 h-5 text-orange-500" />
                <CardTitle>{t(translations.languageTitle)}</CardTitle>
              </div>
              <CardDescription>{t(translations.languageDescription)}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <LanguageOption
                code="system"
                currentValue={languagePreference}
                onClick={handleLanguageChange}
                t={t}
              />
              <LanguageOption
                code="pt-BR"
                currentValue={languagePreference}
                onClick={handleLanguageChange}
                t={t}
              />
              <LanguageOption
                code="en-US"
                currentValue={languagePreference}
                onClick={handleLanguageChange}
                t={t}
              />
            </CardContent>
          </Card>
        </motion.div>

        {/* Notifications Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-orange-500" />
                <CardTitle>{t(translations.notificationsTitle)}</CardTitle>
              </div>
              <CardDescription>{t(translations.notificationsDescription)}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Real Push Notification Toggle */}
              <PushNotificationToggle variant="card" />
              
              <Separator />
              
              <NotificationToggle
                icon={Mail}
                label={t(translations.emailNotifications)}
                description={t(translations.emailDescription)}
                checked={notifications.email}
                onChange={(v) => handleNotificationChange('email', v)}
                testId="toggle-email"
              />
              <NotificationToggle
                icon={MessageSquare}
                label={t(translations.tripReminders)}
                description={t(translations.tripRemindersDescription)}
                checked={notifications.tripReminders}
                onChange={(v) => handleNotificationChange('tripReminders', v)}
                testId="toggle-reminders"
              />
            </CardContent>
          </Card>
        </motion.div>

        {/* PWA Install Section */}
        {(canInstall || !isInstalled) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-orange-500" />
                  <CardTitle>{t(translations.pwaTitle)}</CardTitle>
                </div>
                <CardDescription>{t(translations.pwaDescription)}</CardDescription>
              </CardHeader>
              <CardContent>
                {isInstalled ? (
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400">
                    <Check className="w-5 h-5" />
                    <span className="font-medium">{t(translations.appInstalled)}</span>
                  </div>
                ) : (
                  <Button 
                    onClick={handleInstallApp}
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                    data-testid="install-app-btn"
                  >
                    <Smartphone className="w-4 h-4 mr-2" />
                    {t(translations.installApp)}
                  </Button>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Bottom spacing */}
        <div className="h-8" />
      </div>

      {/* Email Change Modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {emailModalStep === 'form' ? t(translations.changeEmailTitle) : t(translations.verifyEmail)}
            </DialogTitle>
            <DialogDescription>
              {emailModalStep === 'form' 
                ? t(translations.changeEmailDescription)
                : `${t(translations.verifyEmailDescription)} ${newEmail}`
              }
            </DialogDescription>
          </DialogHeader>

          <AnimatePresence mode="wait">
            {emailModalStep === 'form' ? (
              <motion.form
                key="email-form"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                onSubmit={handleInitEmailChange}
                className="space-y-4"
              >
                <div className="space-y-2">
                  <Label htmlFor="new-email">{t(translations.newEmail)}</Label>
                  <Input
                    id="new-email"
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="novo@email.com"
                    required
                    disabled={emailLoading}
                    data-testid="new-email-input"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setEmailModalOpen(false)}
                    disabled={emailLoading}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-orange-500 hover:bg-orange-600"
                    disabled={emailLoading || !newEmail}
                    data-testid="send-code-btn"
                  >
                    {emailLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Mail className="w-4 h-4 mr-2" />
                    )}
                    {t(translations.sendCode)}
                  </Button>
                </div>
              </motion.form>
            ) : (
              <motion.div
                key="verification"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className="text-center py-2">
                  <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Mail className="w-6 h-6 text-orange-500" />
                  </div>
                </div>

                <VerificationCodeInput
                  onComplete={handleVerifyEmailChange}
                  isLoading={verificationLoading}
                  error={verificationError}
                  autoFocus={true}
                />

                <div className="flex flex-col gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleResendEmailCode}
                    disabled={emailLoading}
                    className="w-full"
                    data-testid="resend-email-code-btn"
                  >
                    {emailLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    {t(translations.resendCode)}
                  </Button>
                  
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setEmailModalStep('form')}
                    className="w-full text-slate-600 dark:text-slate-400"
                    data-testid="back-to-email-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t(translations.backToEmail)}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
